import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/state/auth";
import { MediaItem } from "@/types/media";

const QUERY_ROOT = ["admin", "media"] as const;

// Custom fetch for multipart file upload
const uploadFile = async (file: File): Promise<MediaItem> => {
  const token = localStorage.getItem("auth_token");
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch("/api/admin/media", {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Upload failed" }));
    throw new Error(error.detail || "Upload failed");
  }

  const data = await response.json();
  return data.media;
};

const apiRequest = async <T>(url: string, options?: RequestInit): Promise<T> => {
  const token = localStorage.getItem("auth_token");
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Request failed" }));
    throw new Error(error.detail || "Request failed");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
};

export const useMedia = (limit?: number) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: [...QUERY_ROOT, limit],
    queryFn: async () => {
      const url = limit ? `/api/admin/media?limit=${limit}` : "/api/admin/media";
      const data = await apiRequest<{ media: MediaItem[] }>(url);
      return data.media;
    },
    enabled: Boolean(profile),
  });
};

export const useUploadMedia = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      return uploadFile(file);
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT] });
    },
  });
};

export const useDeleteMedia = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: string) => {
      await apiRequest<void>(`/api/admin/media/${mediaId}`, { method: "DELETE" });
      return mediaId;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [...QUERY_ROOT] });
    },
  });
};
